import React, { useState, useEffect } from 'react';
import { Package, Truck, DollarSign, TrendingUp, Clock, CheckCircle, XCircle, RefreshCw, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminShippingDashboard = () => {
  const { user } = useApp();
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [carrierFilter, setCarrierFilter] = useState('');

  useEffect(() => {
    if (user?.id && user?.is_admin) {
      fetchDashboard();
      fetchOrders();
    }
  }, [user, statusFilter, carrierFilter]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API}/api/admin/shipping/dashboard/${user.id}`);
      const data = await response.json();
      setDashboard(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      let url = `${API}/api/admin/shipping/orders/${user.id}`;
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (carrierFilter) params.append('carrier', carrierFilter);
      if (params.toString()) url += `?${params}`;

      const response = await fetch(url);
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      label_generated: 'bg-yellow-100 text-yellow-800',
      pending_shipment: 'bg-orange-100 text-orange-800',
      in_transit: 'bg-blue-100 text-blue-800',
      out_for_delivery: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      delivery_failed: 'bg-red-100 text-red-800',
      returned: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-minted-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Truck className="h-7 w-7 text-minted-600" />
              Shipping Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Manage all shipping operations</p>
          </div>
          <Button onClick={() => { fetchDashboard(); fetchOrders(); }} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Shipments</p>
                    <p className="text-3xl font-bold text-gray-900">{dashboard.total_shipments}</p>
                  </div>
                  <Package className="h-10 w-10 text-minted-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Today's Shipments</p>
                    <p className="text-3xl font-bold text-blue-600">{dashboard.today_shipments}</p>
                  </div>
                  <Clock className="h-10 w-10 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Shipping Profit</p>
                    <p className="text-3xl font-bold text-green-600">${dashboard.total_margin_profit}</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Action</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {dashboard.status_breakdown?.label_generated || 0}
                    </p>
                  </div>
                  <AlertCircle className="h-10 w-10 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Status Breakdown */}
        {dashboard && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">Shipment Status Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {Object.entries(dashboard.status_breakdown || {}).map(([status, count]) => (
                  <div
                    key={status}
                    className={`p-3 rounded-lg text-center cursor-pointer transition-all ${
                      statusFilter === status ? 'ring-2 ring-minted-500' : ''
                    } ${getStatusColor(status)}`}
                    onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
                  >
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs capitalize">{status.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Carrier Breakdown */}
        {dashboard && dashboard.carrier_breakdown && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">By Carrier</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(dashboard.carrier_breakdown).map(([carrier, count]) => (
                  <Button
                    key={carrier}
                    variant={carrierFilter === carrier ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCarrierFilter(carrierFilter === carrier ? '' : carrier)}
                    className={carrierFilter === carrier ? 'bg-minted-600' : ''}
                  >
                    {carrier}: {count}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Orders Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">All Shipments</CardTitle>
            {(statusFilter || carrierFilter) && (
              <Button variant="ghost" size="sm" onClick={() => { setStatusFilter(''); setCarrierFilter(''); }}>
                Clear Filters
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">Order ID</th>
                    <th className="text-left py-3 px-2">Product</th>
                    <th className="text-left py-3 px-2">Buyer</th>
                    <th className="text-left py-3 px-2">Carrier</th>
                    <th className="text-left py-3 px-2">Tracking</th>
                    <th className="text-left py-3 px-2">Status</th>
                    <th className="text-left py-3 px-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-2 font-mono text-xs">{order.id?.slice(0, 8)}</td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          {order.product_image && (
                            <img src={order.product_image} alt="" className="w-8 h-8 rounded object-cover" />
                          )}
                          <span className="truncate max-w-[150px]">{order.product_title}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2">{order.buyer_name}</td>
                      <td className="py-3 px-2">{order.shipping?.carrier}</td>
                      <td className="py-3 px-2 font-mono text-xs">{order.shipping?.tracking_number || '-'}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.shipping?.status)}`}>
                          {order.shipping?.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-gray-600">
                        {order.shipping?.created_at ? new Date(order.shipping.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {orders.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No shipments found
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Missing import
const AlertCircle = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

export default AdminShippingDashboard;
